import { useFtpStore } from '../../store/ftpStore';

/** Rethrows after asking whether the connection is still there. */
export const checkThenThrow = (connId) => (e) => {
  useFtpStore.getState().checkConnection(connId);
  throw e;
};

/**
 * "File Editor" v1: Monaco in a modal, opened from the FTP/SFTP remote pane's
 * Edit context action. Loads the file as text (ftp_read_text — 2 MB / UTF-8
 * guarded in Rust), Ctrl+S or the Save button uploads it back
 * (ftp_write_text).
 *
 * Conflict flow (GitHub-style): every save sends the mtime the buffer was
 * based on; if the server file changed meanwhile, Rust refuses with
 * `remote_changed` and a bar offers Overwrite / View diff / Cancel. The diff
 * view is a side-by-side Monaco DiffEditor — latest server version left, YOUR
 * buffer right and editable — so the merge is: edit the right side, then
 * "Save my version" (which is conflict-checked against the version shown on
 * the left, so a third write in between is caught again).
 */

// Bundled Monaco basic languages only (craco.config.js MonacoWebpackPlugin) —
// anything else edits fine as plaintext. TypeScript highlights as JavaScript:
// Monaco's 'typescript' language would pull in the ~6.5 MB TS worker.
export const EXT_LANGUAGE = {
  json: 'json',
  jsonc: 'json',
  map: 'json',
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  ts: 'javascript',
  tsx: 'javascript',
  mts: 'javascript',
  cts: 'javascript',
  html: 'html',
  htm: 'html',
  vue: 'html',
  css: 'css',
  scss: 'scss',
  sass: 'scss',
  less: 'less',
  xml: 'xml',
  svg: 'xml',
  xsd: 'xml',
  wsdl: 'xml',
  sql: 'sql',
  php: 'php',
  phtml: 'php',
  inc: 'php',
  py: 'python',
  pyw: 'python',
  yml: 'yaml',
  yaml: 'yaml',
  md: 'markdown',
  markdown: 'markdown',
  sh: 'shell',
  bash: 'shell',
  zsh: 'shell',
  ini: 'ini',
  conf: 'ini',
  cfg: 'ini',
  env: 'ini',
  properties: 'ini',
  toml: 'ini',
  java: 'java',
  go: 'go',
  cs: 'csharp',
  c: 'cpp',
  h: 'cpp',
  cpp: 'cpp',
  cc: 'cpp',
  hpp: 'cpp',
  rb: 'ruby',
  rs: 'rust',
  bat: 'bat',
  cmd: 'bat',
  ps1: 'powershell',
  psm1: 'powershell',
  pl: 'perl',
  pm: 'perl',
  lua: 'lua',
  kt: 'kotlin',
  kts: 'kotlin',
  swift: 'swift',
  graphql: 'graphql',
  gql: 'graphql',
};

// Whole file names that say what they are (no extension, or a misleading one).
export const NAME_LANGUAGE = {
  dockerfile: 'dockerfile',
  '.htaccess': 'ini',
  '.env': 'ini',
  '.gitignore': 'ini',
  '.editorconfig': 'ini',
  makefile: 'shell',
};

export function languageForName(name) {
  const base = String(name || '').split(/[\\/]/).pop().toLowerCase();
  if (NAME_LANGUAGE[base]) return NAME_LANGUAGE[base];
  if (base.startsWith('dockerfile')) return 'dockerfile';
  if (base.startsWith('.env.')) return 'ini';
  const ext = base.includes('.') ? base.split('.').pop() : '';
  return EXT_LANGUAGE[ext] || 'plaintext';
}

export const errToken = (e) => {
  const s = String(e?.message || e || '');
  if (s.startsWith('remote_changed')) return 'remote_changed';
  if (s.startsWith('too_large')) return 'too_large';
  if (s.startsWith('not_text')) return 'not_text';
  return null;
};
